/* Aspect-ratio handling for the video player: fit modes, per-show memory and black-bar detection. */
export const FIT_MODES = [
  ["fit", "Fit", "Whole picture visible"],
  ["fill", "Fill", "Zoom to fill the screen, cropping the edges"],
  ["stretch", "Stretch", "Fill the screen ignoring the aspect ratio"],
  ["16:9", "16:9", "Force widescreen (for files with wrong metadata)"],
  ["4:3", "4:3", "Force classic TV shape"],
];
export const nextMode = (m) => FIT_MODES[(FIT_MODES.findIndex(([k]) => k === m) + 1) % FIT_MODES.length][0];
export const modeLabel = (m) => FIT_MODES.find(([k]) => k === m)?.[1] || "Fit";

export const resLabel = (w, h) => {
  if (!w || !h) return "";
  const p = Math.min(w, h), q = Math.max(w, h);
  if (q >= 3800 || p >= 2100) return "4K";
  if (q >= 2500 || p >= 1400) return "QHD";
  if (q >= 1900 || p >= 1050) return "Full HD";
  if (q >= 1260 || p >= 700) return "HD";
  if (p >= 460) return "SD";
  return "Low";
};
export const RES_HINT = { "4K": "Ultra HD (2160p)", QHD: "1440p", "Full HD": "1080p", HD: "720p", SD: "Standard definition (480p / DVD)", Low: "Below DVD quality" };

export const ratioLabel = (r) => {
  if (!r) return "";
  const known = [[1.3333, "4:3"], [1.5, "3:2"], [1.6, "16:10"], [1.7778, "16:9"], [1.85, "1.85:1"], [2.0, "2:1"], [2.2, "2.20:1"], [2.35, "2.35:1"], [2.39, "2.39:1"], [2.4, "2.40:1"], [1, "1:1"], [0.5625, "9:16"]];
  const hit = known.find(([v]) => Math.abs(v - r) < 0.02);
  return hit ? hit[1] : `${r.toFixed(2)}:1`;
};

/* Size the picture inside a stage of W×H. `src` = natural ratio (w/h); `crop` = {t,b,l,r} fractions of baked-in bars to hide.
   Returns { box: {w,h,x,y}, fit, pos } for the <video> element — object-fit does the cropping so native controls stay visible. */
export const layout = (W, H, src, mode, crop) => {
  if (!W || !H) return null;
  const c = crop || { t: 0, b: 0, l: 0, r: 0 };
  const cropping = (mode === "fit" || mode === "fill") && (c.t || c.b || c.l || c.r);
  const kw = cropping ? 1 - c.l - c.r : 1;
  const kh = cropping ? 1 - c.t - c.b : 1;
  const natural = src || 16 / 9;
  const r = mode === "16:9" ? 16 / 9 : mode === "4:3" ? 4 / 3 : (natural * kw) / kh; // visible-region ratio
  const contain = (ratio) => (W / H > ratio ? { w: H * ratio, h: H } : { w: W, h: W / ratio });
  let box = mode === "fill" || mode === "stretch" ? { w: W, h: H } : contain(r);
  box = { ...box, x: (W - box.w) / 2, y: (H - box.h) / 2 };
  const fit = mode === "stretch" || mode === "16:9" || mode === "4:3" ? "fill" : mode === "fill" || cropping ? "cover" : "contain";
  const px = c.l + c.r ? (c.l / (c.l + c.r)) * 100 : 50;
  const py = c.t + c.b ? (c.t / (c.t + c.b)) * 100 : 50;
  return { box, fit, pos: cropping ? `${px}% ${py}%` : "50% 50%", ratio: r };
};

/* Measure baked-in black bars from the current frame. Returns {t,b,l,r} fractions or null when the frame can't be read. */
export const measureBars = (video, canvas, threshold = 28) => {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh || video.readyState < 2) return null;
  const w = 160, h = Math.max(1, Math.round((160 * vh) / vw));
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  let data;
  try { ctx.drawImage(video, 0, 0, w, h); data = ctx.getImageData(0, 0, w, h).data; } catch { return null; }
  const luma = (x, y) => { const i = (y * w + x) * 4; return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]; };
  const rowDark = (y) => { let s = 0; for (let x = 0; x < w; x += 2) s += luma(x, y); return s / (w / 2) < threshold; };
  const colDark = (x) => { let s = 0; for (let y = 0; y < h; y += 2) s += luma(x, y); return s / Math.ceil(h / 2) < threshold; };
  let t = 0, b = 0, l = 0, r = 0;
  while (t < h / 2 && rowDark(t)) t++;
  while (b < h / 2 && rowDark(h - 1 - b)) b++;
  while (l < w / 2 && colDark(l)) l++;
  while (r < w / 2 && colDark(w - 1 - r)) r++;
  const blank = t + b >= h - 2 || l + r >= w - 2; // whole frame dark (fade / black scene) — tells us nothing
  if (blank) return null;
  return { t: t / h, b: b / h, l: l / w, r: r / w };
};

/* Combine several samples conservatively: the bar is only as big as the *smallest* reading, and tiny or absurd values are ignored. */
export const settleCrop = (samples) => {
  if (samples.length < 3) return null;
  const min = (k) => Math.min(...samples.map((s) => s[k]));
  const c = { t: min("t"), b: min("b"), l: min("l"), r: min("r") };
  const clean = (v) => (v >= 0.02 && v <= 0.3 ? v : 0);
  const out = { t: clean(c.t), b: clean(c.b), l: clean(c.l), r: clean(c.r) };
  // keep bars symmetric-ish: letterbox or pillarbox, not both at once (that's a dark scene, not a bar)
  if ((out.t || out.b) && (out.l || out.r)) return null;
  return out.t || out.b || out.l || out.r ? out : null;
};
